import { Component, ViewChildren, QueryList, OnInit } from '@angular/core'
import { Title } from '@angular/platform-browser'
import { ActivatedRoute, Router } from '@angular/router'
import { BaseComponent } from '../../../components/base.component'
import { KeywordPanel } from '../../keyword-panel/keyword-panel.component'
import get from 'lodash-es/get'
import { Subscription } from 'rxjs'
import { KeywordQueries, ICustomer, IContentType, Request } from '../../../graphql/queries/keyword.queries'
import { Data, IKeyword } from '../../../graphql/queries/keyword.queries'
import 'moment-timezone'
import { allowedContentTypes } from '../utils'
import { isVideoPlanCode } from 'src/utils'
import { customerInContext } from 'src/logic/customer-states'
import { keywordStatesByGroup, keywordStates, keywordStateGroups, stateGroups } from 'src/logic/keyword-states'
import { PersistentDropdownService } from 'src/modules/persistent-dropdown/persistent-dropdown.service'

@Component({
  selector: 'keywords-page',
  templateUrl: './keywords.page.html'
})
export class KeywordsPage extends BaseComponent implements OnInit {
  @ViewChildren(KeywordPanel) keywordPanels: QueryList<KeywordPanel>

  adminImpersonating = false
  customerId: number = null
  keywords: IKeyword[] = [] // Just for empty state on search
  requests: Request[] = []
  keywordsByStateGroup: object = null
  loading = true
  contentTypesEnabled = false
  blogContentTypeId = 1
  showHelpModal = false
  querySub: Subscription
  customer: ICustomer
  contentTypes: IContentType[]
  showPaginator:boolean
  totalCount: number
  page: number
  pageSize: number
  dropDownName = 'keyword-sort'
  defaultOrder = 'updatedAt DESC'
  order: string

  filters: {
    stateGroup: string,
    search: string,
  } = {
    stateGroup: null,
    search: '',
  }

  constructor(
    private route: ActivatedRoute,
    private keywordQueries: KeywordQueries,
    private titleService: Title,
    private router: Router,
    private dropdownService: PersistentDropdownService
  ) {
    super()
  }

  ngOnInit(): void {
    this.order = this.dropdownService.getSelectedValue(this.dropDownName, this.defaultOrder)
    if (!this.keywordsByStateGroup) {
      this.initiateKeywordsByStateGroup([])
    }
    const queryParamsObs = this.route.queryParams.subscribe(queryParams => {
      if (queryParams.order && queryParams.order[queryParams.order.length - 1] == '!') {
        this.order = queryParams.order.substring(0, queryParams.order.length - 1)
      }
      this.page = queryParams['page'] ? Number(queryParams['page']) : 1

      const customSize = Number(localStorage.getItem('customerPageSize')) || 25
      this.pageSize = queryParams['size'] ? Number(queryParams['size']) : customSize

      this.clearRouteObservers()

      this.updateFilters(queryParams)

      const customerIdObs = this.route.parent.parent.params.subscribe(customerIdParams => {
        this.clearRouteObservers()
        this.customerId = +customerIdParams['customerId']

        this.titleService.setTitle('Requests')
        if (this.customerId) {
          this.performQuery()
        }
      })
      this.observers.push(customerIdObs)
    })

    this.observers.push(queryParamsObs)
  }

  ngOnDestroy(): void {
    if (this.querySub) this.querySub.unsubscribe()
  }

  get activeKeywordGroupCount(): number {
    return get(this.customer, 'activeKeywordsCount', 0) + get(this.customer, 'assignedKeywordsCount', 0)
  }

  get showNoActiveKeywordsWarning(): boolean {
    const activeCount = this.activeKeywordGroupCount + get(this.customer, 'activeRequestsCount', 0)
    const customerState = get(this.customer, 'state', null)
    return activeCount <= 0 &&
      customerState !== 'pending' &&
      this.filters.stateGroup === 'active' &&
      !this.loading
  }

  get isVideoSubscription(): boolean {
    if (this.customer && this.customer.plan) {
      return isVideoPlanCode(this.customer.plan.code)
    }
    return false
  }

  get isCustomerCurrent(): boolean {
    return this.customer && customerInContext(this.customer, 'current')
  }

  orderChange(event: string) {
    this.page = 1
    this.order = event
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        'page': this.page,
        'order': this.order
      },
      queryParamsHandling: 'merge'
    })
  }

  keywordStatesByGroup = keywordStatesByGroup

  get selectedStates(): string[] {
    return keywordStatesByGroup[this.filters.stateGroup]
  }

  get selectedStateGroups(): string[] { // needed for 'All' search
    return this.filters.stateGroup ? [this.filters.stateGroup] : keywordStateGroups
  }

  showEmptyState(group): boolean {
    return this.filters.stateGroup !== 'active' &&
      !this.filters.search &&
      this.keywordsByStateGroup[group].length === 0 &&
      !this.loading
  }

  filterTypes(types: IContentType[]): IContentType[] {
    if (this.customer?.multipass) {
      return types
    }

    return allowedContentTypes(types, get(this.customer, 'plan.words'))
  }

  onPageChange(event): void {
    localStorage.setItem('customerPageSize', event.pageSize.toString())
    const pageIndex = event.pageIndex + 1
    const pageSize = event.pageSize
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        'page': pageIndex,
        'size': pageSize
      },
      queryParamsHandling: 'merge'
    })
  }

  initiateKeywordsByStateGroup(keywords: IKeyword[]): void {
    this.keywordsByStateGroup = keywordStateGroups.reduce((o, group) => ({
      ...o,
      [group]: keywords ? this.keywordsInStateGroup(keywords, group) : []
    }), {})
  }

  private keywordsInStateGroup(keywords: IKeyword[], group: string): IKeyword[] {
    return keywords.filter(k => stateGroups[group].includes(k.state))
  }

  private updateFilters(queryParams): void {
    this.filters.search = queryParams['search']
    this.filters.stateGroup = this.parseQueryState(queryParams['group'] || queryParams['state'] || queryParams['states'])
  }

  private parseQueryState(queryState: string = null): string {
    if (get(this.filters, 'search', null)) {
      return queryState
    } else if (queryState) {
      return queryState
    } else {
      return 'active'
    }
  }

  private performQuery = (): void => {
    this.loading = true
    const search = this.filters.search
    const states = this.filters.stateGroup ? keywordStatesByGroup(this.filters.stateGroup) : keywordStates
    if (this.filters.search) {
      this.querySub = this.keywordQueries.searchQuery(this.customerId, { search, states }, this.page, this.pageSize, this.order).subscribe(data => {
        this.setData(data)
      })
    } else {
      this.querySub = this.keywordQueries.mainQuery(this.customerId, states, this.page, this.pageSize, this.order).subscribe(data => {
        this.setData(data)
      })
    }
  }

  private setData = ({ data, loading, stale }: { data: Data, loading: boolean, stale: boolean }): void => {
    this.loading = loading && stale
    if (data) {
      this.adminImpersonating = get(data, 'currentUser.adminImpersonating', false)
      this.customer = data.customer
      this.keywords = this.customer.keywords.entries?.filter(k => k.kind != "proposed" || k.state != "declined")
      this.requests = this.customer.requests.entries || null
      this.titleService.setTitle('Dashboard' + (this.customer ? ` - ${this.customer.businessName}` : ''))
      this.contentTypesEnabled = data.account &&
                                 data.account.enabledFeatures.includes('content_types') &&
                                 this.customer.multipass ? true : get(this.customer, 'plan.words', false)
      this.contentTypes = data.contentTypes

      if (this.keywords) {
        this.initiateKeywordsByStateGroup(this.keywords)
        this.totalCount = this.customer.keywords.totalCount
      } else if (this.requests) {
        this.totalCount = this.customer.requests.totalCount
      }
      this.showPaginator = this.totalCount > this.pageSize
      this.loading = false
    }
  }
}
